'use client'

import { m, useReducedMotion } from 'motion/react'

export function Reveal({
    children,
    className,
    delay = 0,
}: {
    children: React.ReactNode
    className?: string
    delay?: number
}) {
    const reduce = useReducedMotion()

    if (reduce) {
        return <div className={className}>{children}</div>
    }

    return (
        <m.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '0px 0px -8% 0px' }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay }}
            className={className}
        >
            {children}
        </m.div>
    )
}

/**
 * RevealStamp — ink-stamp / hanko press-in motion.
 * Used for the colophon seal. Slight overshoot on scale, brief rotation,
 * fast settle. Reads as "pressed in", not "faded in".
 */
export function RevealStamp({
    children,
    className,
    delay = 0,
}: {
    children: React.ReactNode
    className?: string
    delay?: number
}) {
    const reduce = useReducedMotion()

    if (reduce) {
        return <div className={className}>{children}</div>
    }

    return (
        <m.div
            initial={{ opacity: 0, scale: 0.6, rotate: -10 }}
            whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
            viewport={{ once: true, margin: '0px 0px -10% 0px' }}
            transition={{
                duration: 0.7,
                ease: [0.34, 1.56, 0.64, 1],
                delay,
            }}
            className={className}
        >
            {children}
        </m.div>
    )
}

/**
 * RevealRule — hairline rule "drawn" across, then content reveals beneath.
 * For editorial section rows (Path entries). Subtle but distinctive.
 */
export function RevealRule({
    children,
    className,
    delay = 0,
}: {
    children: React.ReactNode
    className?: string
    delay?: number
}) {
    const reduce = useReducedMotion()

    if (reduce) {
        return <div className={className}>{children}</div>
    }

    return (
        <m.div
            initial={{ opacity: 0, x: -12, clipPath: 'inset(0 100% 0 0)' }}
            whileInView={{ opacity: 1, x: 0, clipPath: 'inset(0 0% 0 0)' }}
            viewport={{ once: true, margin: '0px 0px -6% 0px' }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1], delay }}
            className={className}
        >
            {children}
        </m.div>
    )
}
